import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import SettingService from "services/SettingService";

export const initialState = {
  loading: false,
  message: "",
  messageKey: "",
  showMessage: false,
  settingData: {},
  reviews: [],
  total: 0,
  paymentKeys: {},
  stripeKeys: {},
  cbkKeys: {},
  tabbyKeys: {},
  upaymentKeys: {},
  talyKeys: {},
  allowmenaKeys: {},
  tehseelKeys: {},
  shipping: {},
  metaKeys: {},
  metaCredentials: {},
  zones: [],
  zoneDeliveryPrices: [],
  areas: [],
  areaDeliveryPrices: [],
  redirect: "",
  // Country Settings
  countrySettings: {
    assigned_country_ids: [],
    assigned_countries: [],
    all_countries: [],
    zones: [],
    areas: [],
  },
  countryZones: [],
  managedAreas: [],
  // Enhanced Shipping - NEW
  shippingConfig: [],
  countryShipping: [],
  zoneShipping: [],
  areaShipping: [],
  legacySettings: [],
  // Footer Settings
  footerSettings: null,
  // AI Footer Builder
  generatedFooterCode: "",
  generatingFooterCode: false,
  // Header Settings
  headerSettings: null,
  // SMS Settings
  smsSettings: null,
  smsLoading: false,
  // Pixel Settings
  pixelSettings: null,
  pixelLoading: false,
  // Theme Colors
  themeColors: null,
  themeColorsLoading: false,
  firebaseWebSettings: null,
  firebaseWebLoading: false,
};

export const getSetting = createAsyncThunk(
  "setting/getSetting",
  async (_, { rejectWithValue }) => {
    try {
      const response = await SettingService.getSetting();
      return response.data;
    } catch (err) {
      return rejectWithValue({
        message: err.message || "Error",
        messageKey: "setting.fetch_error",
      });
    }
  },
);

export const updateSetting = createAsyncThunk(
  "setting/updateSetting",
  async (data, { rejectWithValue }) => {
    try {
      const response = await SettingService.updateSetting(data);
      return response.data;
    } catch (err) {
      return rejectWithValue({
        message: err.message || "Error",
        messageKey: "setting.update_error",
      });
    }
  },
);

export const getReviews = createAsyncThunk(
  "setting/getReviews",
  async ({ page = 1, languageId = "1" }, { rejectWithValue }) => {
    try {
      const response = await SettingService.reviews(page, {
        "Language-Id": languageId,
      });
      return response;
    } catch (err) {
      return rejectWithValue({
        message: err.message || "Error",
        messageKey: "setting.reviews_fetch_error",
      });
    }
  },
);

export const updateReviewStatus = createAsyncThunk(
  "setting/updateReviewStatus",
  async ({ reviewId, status }, { rejectWithValue }) => {
    try {
      const response = await SettingService.updateReviewStatus(reviewId, status);
      return { reviewId, status, response };
    } catch (err) {
      return rejectWithValue({ message: err.message || "Error" });
    }
  },
);

export const deleteReview = createAsyncThunk(
  "setting/deleteReview",
  async (reviewId, { rejectWithValue }) => {
    try {
      await SettingService.deleteReview(reviewId);
      return reviewId;
    } catch (err) {
      return rejectWithValue({ message: err.message || "Error" });
    }
  },
);

export const getPaymentKeys = createAsyncThunk(
  "setting/getPaymentKeys",
  async (_, { rejectWithValue }) => {
    try {
      const response = await SettingService.paymentKeys();
      return response.data;
    } catch (err) {
      return rejectWithValue({
        message: err.message || "Error",
        messageKey: "setting.payment_keys_error",
      });
    }
  },
);

export const getShipping = createAsyncThunk(
  "setting/getShipping",
  async (_, { rejectWithValue }) => {
    try {
      const response = await SettingService.shipping();
      return response.data;
    } catch (err) {
      return rejectWithValue({
        message: err.message || "Error",
        messageKey: "setting.shipping_fetch_error",
      });
    }
  },
);

export const getZones = createAsyncThunk(
  "setting/getZones",
  async (_, { rejectWithValue }) => {
    try {
      const response = await SettingService.getZone();
      console.log("Raw zones response:", response); // Debug log

      // Handle nested response structure
      if (response.data && response.data.data) {
        return response.data.data; // Extract the zones array from nested structure
      } else if (response.data) {
        return response.data;
      } else {
        return response;
      }
    } catch (err) {
      return rejectWithValue({
        message: err.message || "Error",
        messageKey: "setting.zones_fetch_error",
      });
    }
  },
);

// Zone Delivery Prices thunks
export const getZoneDeliveryPrices = createAsyncThunk(
  "setting/getZoneDeliveryPrices",
  async (_, { rejectWithValue }) => {
    try {
      const response = await SettingService.getZoneDeliveryPrices();
      console.log("Raw zone delivery prices response:", response);

      // Handle nested response: {success: true, data: [...]}
      if (response.data && response.data.data) {
        return response.data.data;
      } else if (response.data) {
        return response.data;
      } else {
        return response;
      }
    } catch (err) {
      return rejectWithValue({
        message: err.message || "Error",
        messageKey: "zone.fetch_error",
      });
    }
  },
);

export const createZoneDeliveryPrice = createAsyncThunk(
  "setting/createZoneDeliveryPrice",
  async (data, { rejectWithValue }) => {
    try {
      const response = await SettingService.createZoneDeliveryPrice(data);
      return response.data;
    } catch (err) {
      return rejectWithValue({
        message: err.message || "Error",
        messageKey: "zone.create_error",
      });
    }
  },
);

export const updateZoneDeliveryPrice = createAsyncThunk(
  "setting/updateZoneDeliveryPrice",
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const response = await SettingService.updateZoneDeliveryPrice(id, data);
      return response.data;
    } catch (err) {
      return rejectWithValue({
        message: err.message || "Error",
        messageKey: "zone.update_error",
      });
    }
  },
);

export const deleteZoneDeliveryPrice = createAsyncThunk(
  "setting/deleteZoneDeliveryPrice",
  async (id, { rejectWithValue }) => {
    try {
      await SettingService.deleteZoneDeliveryPrice(id);
      return id;
    } catch (err) {
      return rejectWithValue({
        message: err.message || "Error",
        messageKey: "zone.delete_error",
      });
    }
  },
);

export const bulkUpdateZoneDeliveryPrices = createAsyncThunk(
  "setting/bulkUpdateZoneDeliveryPrices",
  async (data, { rejectWithValue }) => {
    try {
      const response = await SettingService.bulkUpdateZoneDeliveryPrices(data);
      return response.data;
    } catch (err) {
      return rejectWithValue({
        message: err.message || "Error",
        messageKey: "zone.bulk_update_error",
      });
    }
  },
);

export const getMetaKeys = createAsyncThunk(
  "setting/getMetaKeys",
  async (_, { rejectWithValue }) => {
    try {
      const response = await SettingService.meta_keys();
      return response.data;
    } catch (err) {
      return rejectWithValue({
        message: err.message || "Error",
        messageKey: "setting.meta_keys_error",
      });
    }
  },
);

export const getMetaCredentials = createAsyncThunk(
  "setting/getMetaCredentials",
  async (_, { rejectWithValue }) => {
    try {
      const response = await SettingService.getMetaCredentials();
      return response;
    } catch (err) {
      return rejectWithValue({
        message: err.message || "Error",
        messageKey: "setting.meta_credentials_error",
      });
    }
  },
);

export const updateMetaCredentials = createAsyncThunk(
  "setting/updateMetaCredentials",
  async (data, { rejectWithValue }) => {
    try {
      const response = await SettingService.updateMetaCredentials(data);
      return response.data;
    } catch (err) {
      return rejectWithValue({
        message: err.message || "Error",
        messageKey: "setting.meta_update_error",
      });
    }
  },
);

// Area thunks
export const getAreasByZone = createAsyncThunk(
  "setting/getAreasByZone",
  async (zoneId, { rejectWithValue }) => {
    try {
      const response = await SettingService.getAreasByZone(zoneId);
      return response.data;
    } catch (err) {
      return rejectWithValue({
        message: err.message || "Error",
        messageKey: "area.fetch_error",
      });
    }
  },
);

export const getAreaDeliveryPrices = createAsyncThunk(
  "setting/getAreaDeliveryPrices",
  async (_, { rejectWithValue }) => {
    try {
      const response = await SettingService.getAreaDeliveryPrices();
      return response.data;
    } catch (err) {
      return rejectWithValue({
        message: err.message || "Error",
        messageKey: "area.fetch_error",
      });
    }
  },
);

export const createAreaDeliveryPrice = createAsyncThunk(
  "setting/createAreaDeliveryPrice",
  async (data, { rejectWithValue }) => {
    try {
      const response = await SettingService.createAreaDeliveryPrice(data);
      return response.data;
    } catch (err) {
      return rejectWithValue({
        message: err.message || "Error",
        messageKey: "area.create_error",
      });
    }
  },
);

export const updateAreaDeliveryPrice = createAsyncThunk(
  "setting/updateAreaDeliveryPrice",
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const response = await SettingService.updateAreaDeliveryPrice(id, data);
      return response.data;
    } catch (err) {
      return rejectWithValue({
        message: err.message || "Error",
        messageKey: "area.update_error",
      });
    }
  },
);

export const deleteAreaDeliveryPrice = createAsyncThunk(
  "setting/deleteAreaDeliveryPrice",
  async (id, { rejectWithValue }) => {
    try {
      await SettingService.deleteAreaDeliveryPrice(id);
      return id;
    } catch (err) {
      return rejectWithValue({
        message: err.message || "Error",
        messageKey: "area.delete_error",
      });
    }
  },
);

export const bulkCreateAreaDeliveryPrices = createAsyncThunk(
  "setting/bulkCreateAreaDeliveryPrices",
  async (data, { rejectWithValue }) => {
    try {
      const response = await SettingService.bulkCreateAreaDeliveryPrices(data);
      return response.data;
    } catch (err) {
      return rejectWithValue({
        message: err.message || "Error",
        messageKey: "area.bulk_create_error",
      });
    }
  },
);

export const getStripeKeys = createAsyncThunk(
  "setting/getStripeKeys",
  async (_, { rejectWithValue }) => {
    try {
      const response = await SettingService.getStripeKeys();
      return response.data;
    } catch (err) {
      return rejectWithValue({
        message: err.message || "Error",
        messageKey: "setting.stripe_keys_error",
      });
    }
  },
);

export const updateStripeKeys = createAsyncThunk(
  "setting/updateStripeKeys",
  async (data, { rejectWithValue }) => {
    try {
      const response = await SettingService.updateStripeKeys(data);
      return response.data;
    } catch (err) {
      return rejectWithValue({
        message: err.message || "Error",
        messageKey: "setting.stripe_update_error",
      });
    }
  },
);

// CBK Payment Keys thunks
export const getCbkKeys = createAsyncThunk(
  "setting/getCbkKeys",
  async (_, { rejectWithValue }) => {
    try {
      const response = await SettingService.getCbkKeys();
      return response.data;
    } catch (err) {
      return rejectWithValue({
        message: err.message || "Error",
        messageKey: "setting.cbk_keys_error",
      });
    }
  },
);

export const updateCbkKeys = createAsyncThunk(
  "setting/updateCbkKeys",
  async (data, { rejectWithValue }) => {
    try {
      const response = await SettingService.updateCbkKeys(data);
      return response;
    } catch (err) {
      return rejectWithValue({
        message: err.message || "Error",
        messageKey: "setting.cbk_update_error",
      });
    }
  },
);

// Tabby Payment Keys thunks
export const getTabbyKeys = createAsyncThunk(
  "setting/getTabbyKeys",
  async (_, { rejectWithValue }) => {
    try {
      const response = await SettingService.getTabbyKeys();
      return response;
    } catch (err) {
      return rejectWithValue({
        message: err.message || "Error",
        messageKey: "setting.tabby_fetch_error",
      });
    }
  },
);

export const updateTabbyKeys = createAsyncThunk(
  "setting/updateTabbyKeys",
  async (data, { rejectWithValue }) => {
    try {
      const response = await SettingService.updateTabbyKeys(data);
      return response;
    } catch (err) {
      return rejectWithValue({
        message: err.message || "Error",
        messageKey: "setting.tabby_update_error",
      });
    }
  },
);

// UPayment Keys thunks
export const getUPaymentKeys = createAsyncThunk(
  "setting/getUPaymentKeys",
  async (_, { rejectWithValue }) => {
    try {
      const response = await SettingService.getUPaymentKeys();
      return response;
    } catch (err) {
      return rejectWithValue({
        message: err.message || "Error",
        messageKey: "setting.upayment_fetch_error",
      });
    }
  },
);

export const updateUPaymentKeys = createAsyncThunk(
  "setting/updateUPaymentKeys",
  async (data, { rejectWithValue }) => {
    try {
      const response = await SettingService.updateUPaymentKeys(data);
      return response;
    } catch (err) {
      return rejectWithValue({
        message: err.message || "Error",
        messageKey: "setting.upayment_update_error",
      });
    }
  },
);

// Taly Keys thunks
export const getTalyKeys = createAsyncThunk(
  "setting/getTalyKeys",
  async (_, { rejectWithValue }) => {
    try {
      const response = await SettingService.getTalyKeys();
      return response;
    } catch (err) {
      return rejectWithValue({
        message: err.message || "Error",
        messageKey: "setting.taly_fetch_error",
      });
    }
  },
);

export const updateTalyKeys = createAsyncThunk(
  "setting/updateTalyKeys",
  async (data, { rejectWithValue }) => {
    try {
      const response = await SettingService.updateTalyKeys(data);
      return response;
    } catch (err) {
      return rejectWithValue({
        message: err.message || "Error",
        messageKey: "setting.taly_update_error",
      });
    }
  },
);

export const getAllowmenaKeys = createAsyncThunk(
  "setting/getAllowmenaKeys",
  async (_, { rejectWithValue }) => {
    try {
      const response = await SettingService.getAllowmenaKeys();
      return response;
    } catch (err) {
      return rejectWithValue({
        message: err.message || "Error",
        messageKey: "setting.allowmena_fetch_error",
      });
    }
  },
);

export const updateAllowmenaKeys = createAsyncThunk(
  "setting/updateAllowmenaKeys",
  async (data, { rejectWithValue }) => {
    try {
      const response = await SettingService.updateAllowmenaKeys(data);
      return response;
    } catch (err) {
      return rejectWithValue({
        message: err.message || "Error",
        messageKey: "setting.allowmena_update_error",
      });
    }
  },
);

// Tehseel Keys thunks
export const getTehseelKeys = createAsyncThunk(
  "setting/getTehseelKeys",
  async (_, { rejectWithValue }) => {
    try {
      const response = await SettingService.getTehseelKeys();
      return response;
    } catch (err) {
      return rejectWithValue({
        message: err.message || "Error",
        messageKey: "setting.tehseel_fetch_error",
      });
    }
  },
);

export const updateTehseelKeys = createAsyncThunk(
  "setting/updateTehseelKeys",
  async (data, { rejectWithValue }) => {
    try {
      const response = await SettingService.updateTehseelKeys(data);
      return response;
    } catch (err) {
      return rejectWithValue({
        message: err.message || "Error",
        messageKey: "setting.tehseel_update_error",
      });
    }
  },
);

// Email Settings thunks
export const getEmailSettings = createAsyncThunk(
  "setting/getEmailSettings",
  async (_, { rejectWithValue }) => {
    try {
      const response = await SettingService.getEmailSettings();
      return response.data;
    } catch (err) {
      return rejectWithValue({
        message: err.message || "Error",
        messageKey: "email_settings.fetch_error",
      });
    }
  },
);

export const updateEmailSettings = createAsyncThunk(
  "setting/updateEmailSettings",
  async (data, { rejectWithValue }) => {
    try {
      const response = await SettingService.updateEmailSettings(data);
      return response.data;
    } catch (err) {
      return rejectWithValue({
        message: err.message || "Error",
        messageKey: "email_settings.update_error",
      });
    }
  },
);

export const resetEmailSettings = createAsyncThunk(
  "setting/resetEmailSettings",
  async (_, { rejectWithValue }) => {
    try {
      const response = await SettingService.resetEmailSettings();
      return response.data;
    } catch (err) {
      return rejectWithValue({
        message: err.message || "Error",
        messageKey: "email_settings.reset_error",
      });
    }
  },
);

// Country Settings thunks
export const getCountrySettings = createAsyncThunk(
  "setting/getCountrySettings",
  async (_, { rejectWithValue }) => {
    try {
      const response = await SettingService.getCountrySettings();
      console.log("Country settings response:", response);
      return response.data;
    } catch (err) {
      return rejectWithValue({
        message: err.message || "Error",
        messageKey: "country_settings_fetch_error",
      });
    }
  },
);

export const updateCountrySettings = createAsyncThunk(
  "setting/updateCountrySettings",
  async (data, { rejectWithValue }) => {
    try {
      const response = await SettingService.updateCountrySettings(data);
      return response.data;
    } catch (err) {
      return rejectWithValue({
        message: err.message || "Error",
        messageKey: "country_settings_update_error",
      });
    }
  },
);

export const deleteCountrySettings = createAsyncThunk(
  "setting/deleteCountrySettings",
  async (_, { rejectWithValue }) => {
    try {
      const response = await SettingService.deleteCountrySettings();
      return response.data;
    } catch (err) {
      return rejectWithValue({
        message: err.message || "Error",
        messageKey: "country_settings_delete_error",
      });
    }
  },
);

export const getCountryZones = createAsyncThunk(
  "setting/getCountryZones",
  async ({ countryId = null, zoneId = null } = {}, { rejectWithValue }) => {
    try {
      const response = await SettingService.getCountryZones(countryId, zoneId);
      return response.data;
    } catch (err) {
      return rejectWithValue({
        message: err.message || "Error",
        messageKey: "country_settings_zones_fetch_error",
      });
    }
  },
);

// Area Management thunks
export const getManagedAreas = createAsyncThunk(
  "setting/getManagedAreas",
  async (zoneId, { rejectWithValue }) => {
    try {
      const response = await SettingService.getAreas(zoneId);
      return response.data;
    } catch (err) {
      return rejectWithValue({
        message: err.message || "Error",
        messageKey: "area_fetch_error",
      });
    }
  },
);

export const createManagedArea = createAsyncThunk(
  "setting/createManagedArea",
  async (data, { rejectWithValue }) => {
    try {
      const response = await SettingService.createArea(data);
      return response.data;
    } catch (err) {
      return rejectWithValue({
        message: err.message || "Error",
        messageKey: "area_create_error",
      });
    }
  },
);

export const updateManagedArea = createAsyncThunk(
  "setting/updateManagedArea",
  async (data, { rejectWithValue }) => {
    try {
      const response = await SettingService.updateArea(data);
      return response.data;
    } catch (err) {
      return rejectWithValue({
        message: err.message || "Error",
        messageKey: "area_update_error",
      });
    }
  },
);

export const deleteManagedArea = createAsyncThunk(
  "setting/deleteManagedArea",
  async (areaId, { rejectWithValue }) => {
    try {
      await SettingService.deleteArea(areaId);
      return areaId;
    } catch (err) {
      return rejectWithValue({
        message: err.message || "Error",
        messageKey: "area_delete_error",
      });
    }
  },
);

export const bulkCreateManagedAreas = createAsyncThunk(
  "setting/bulkCreateManagedAreas",
  async (data, { rejectWithValue }) => {
    try {
      const response = await SettingService.bulkCreateAreas(data);
      return response.data;
    } catch (err) {
      return rejectWithValue({
        message: err.message || "Error",
        messageKey: "area_bulk_create_error",
      });
    }
  },
);

// Enhanced Shipping thunks
export const getShippingConfig = createAsyncThunk(
  "setting/getShippingConfig",
  async (_, { rejectWithValue }) => {
    try {
      const response = await SettingService.shipping();
      console.log("Shipping config response:", response);
      return response.data;
    } catch (err) {
      return rejectWithValue({
        message: err.message || "Error",
        messageKey: "shipping.fetch_error",
      });
    }
  },
);

// Country Shipping
export const getCountryShipping = createAsyncThunk(
  "setting/getCountryShipping",
  async (countryId = null, { rejectWithValue }) => {
    try {
      const response = await SettingService.getCountryShipping(countryId);
      return response.data;
    } catch (err) {
      return rejectWithValue({
        message: err.message || "Error",
        messageKey: "shipping.country_fetch_error",
      });
    }
  },
);

export const updateCountryShipping = createAsyncThunk(
  "setting/updateCountryShipping",
  async (data, { rejectWithValue }) => {
    try {
      const response = await SettingService.updateCountryShipping(data);
      return response.data;
    } catch (err) {
      return rejectWithValue({
        message: err.message || "Error",
        messageKey: "shipping.country_update_error",
      });
    }
  },
);

export const deleteCountryShipping = createAsyncThunk(
  "setting/deleteCountryShipping",
  async (countryId, { rejectWithValue }) => {
    try {
      await SettingService.deleteCountryShipping(countryId);
      return countryId;
    } catch (err) {
      return rejectWithValue({
        message: err.message || "Error",
        messageKey: "shipping.country_delete_error",
      });
    }
  },
);

// Zone Shipping
export const getZoneShipping = createAsyncThunk(
  "setting/getZoneShipping",
  async ({ zoneId = null, countryId = null } = {}, { rejectWithValue }) => {
    try {
      const response = await SettingService.getZoneShipping(zoneId, countryId);
      return response.data;
    } catch (err) {
      return rejectWithValue({
        message: err.message || "Error",
        messageKey: "shipping.zone_fetch_error",
      });
    }
  },
);

export const updateZoneShipping = createAsyncThunk(
  "setting/updateZoneShipping",
  async (data, { rejectWithValue }) => {
    try {
      const response = await SettingService.updateZoneShipping(data);
      return response.data;
    } catch (err) {
      return rejectWithValue({
        message: err.message || "Error",
        messageKey: "shipping.zone_update_error",
      });
    }
  },
);

export const deleteZoneShipping = createAsyncThunk(
  "setting/deleteZoneShipping",
  async (zoneId, { rejectWithValue }) => {
    try {
      await SettingService.deleteZoneShipping(zoneId);
      return zoneId;
    } catch (err) {
      return rejectWithValue({
        message: err.message || "Error",
        messageKey: "shipping.zone_delete_error",
      });
    }
  },
);

// Area Shipping
export const getAreaShipping = createAsyncThunk(
  "setting/getAreaShipping",
  async (
    { areaId = null, zoneId = null, countryId = null } = {},
    { rejectWithValue },
  ) => {
    try {
      const response = await SettingService.getAreaShipping(
        areaId,
        zoneId,
        countryId,
      );
      return response.data;
    } catch (err) {
      return rejectWithValue({
        message: err.message || "Error",
        messageKey: "shipping.area_fetch_error",
      });
    }
  },
);

export const updateAreaShipping = createAsyncThunk(
  "setting/updateAreaShipping",
  async (data, { rejectWithValue }) => {
    try {
      const response = await SettingService.updateAreaShipping(data);
      return response.data;
    } catch (err) {
      return rejectWithValue({
        message: err.message || "Error",
        messageKey: "shipping.area_update_error",
      });
    }
  },
);

export const deleteAreaShipping = createAsyncThunk(
  "setting/deleteAreaShipping",
  async (areaId, { rejectWithValue }) => {
    try {
      await SettingService.deleteAreaShipping(areaId);
      return areaId;
    } catch (err) {
      return rejectWithValue({
        message: err.message || "Error",
        messageKey: "shipping.area_delete_error",
      });
    }
  },
);

// Bulk Shipping
export const bulkUpdateShipping = createAsyncThunk(
  "setting/bulkUpdateShipping",
  async (data, { rejectWithValue }) => {
    try {
      const response = await SettingService.bulkUpdateShipping(data);
      return response.data;
    } catch (err) {
      return rejectWithValue({
        message: err.message || "Error",
        messageKey: "shipping.bulk_update_error",
      });
    }
  },
);

// Footer Settings thunks
export const getFooterSettings = createAsyncThunk(
  "setting/getFooterSettings",
  async (_, { rejectWithValue }) => {
    try {
      const response = await SettingService.getFooterSettings();
      return response.data;
    } catch (err) {
      return rejectWithValue({
        message: err.message || "Error",
        messageKey: "footer_settings.fetch_error",
      });
    }
  },
);

export const updateFooterSettings = createAsyncThunk(
  "setting/updateFooterSettings",
  async (data, { rejectWithValue }) => {
    try {
      const response = await SettingService.updateFooterSettings(data);
      return response.data;
    } catch (err) {
      return rejectWithValue({
        message: err.message || "Error",
        messageKey: "footer_settings.update_error",
      });
    }
  },
);

export const generateFooterCode = createAsyncThunk(
  "setting/generateFooterCode",
  async (data, { rejectWithValue }) => {
    try {
      const response = await SettingService.generateFooterCode(data);
      return response.data;
    } catch (err) {
      return rejectWithValue({
        message: err.message || "Error",
        messageKey: "footer_builder.generate_error",
      });
    }
  },
);

// Header Settings thunks
export const getHeaderSettings = createAsyncThunk(
  "setting/getHeaderSettings",
  async (_, { rejectWithValue }) => {
    try {
      const response = await SettingService.getHeaderSettings();
      return response.data;
    } catch (err) {
      return rejectWithValue({
        message: err.message || "Error",
        messageKey: "header_settings.fetch_error",
      });
    }
  },
);

export const updateHeaderSettings = createAsyncThunk(
  "setting/updateHeaderSettings",
  async (data, { rejectWithValue }) => {
    try {
      const response = await SettingService.updateHeaderSettings(data);
      return response.data;
    } catch (err) {
      return rejectWithValue({
        message: err.message || "Error",
        messageKey: "header_settings.update_error",
      });
    }
  },
);

// SMS Settings thunks
export const getSmsSettings = createAsyncThunk(
  "setting/getSmsSettings",
  async (_, { rejectWithValue }) => {
    try {
      const response = await SettingService.getSmsSettings();
      return response;
    } catch (err) {
      return rejectWithValue({
        message: err.message || "Error",
        messageKey: "sms_settings.fetch_error",
      });
    }
  },
);

export const updateSmsSettings = createAsyncThunk(
  "setting/updateSmsSettings",
  async (data, { rejectWithValue }) => {
    try {
      const response = await SettingService.updateSmsSettings(data);
      return response;
    } catch (err) {
      return rejectWithValue({
        message: err.message || "Error",
        messageKey: "sms_settings.update_error",
      });
    }
  },
);

// Pixel Settings thunks
export const getPixelSettings = createAsyncThunk(
  "setting/getPixelSettings",
  async (_, { rejectWithValue }) => {
    try {
      const response = await SettingService.getPixelSettings();
      return response;
    } catch (err) {
      return rejectWithValue({
        message: err.message || "Error",
        messageKey: "pixel_settings.fetch_error",
      });
    }
  },
);

export const updatePixelSettings = createAsyncThunk(
  "setting/updatePixelSettings",
  async (data, { rejectWithValue }) => {
    try {
      const response = await SettingService.updatePixelSettings(data);
      return response;
    } catch (err) {
      return rejectWithValue({
        message: err.message || "Error",
        messageKey: "pixel_settings.update_error",
      });
    }
  },
);

export const getFirebaseWebSettings = createAsyncThunk(
  "setting/getFirebaseWebSettings",
  async (_, { rejectWithValue }) => {
    try {
      const response = await SettingService.getFirebaseWebSettings();
      return response;
    } catch (err) {
      return rejectWithValue({
        message: err.message || "Error",
        messageKey: "firebase_settings.fetch_error",
      });
    }
  },
);

export const updateFirebaseWebSettings = createAsyncThunk(
  "setting/updateFirebaseWebSettings",
  async (data, { rejectWithValue }) => {
    try {
      const response = await SettingService.updateFirebaseWebSettings(data);
      return response;
    } catch (err) {
      return rejectWithValue({
        message: err.message || "Error",
        messageKey: "firebase_settings.update_error",
      });
    }
  },
);

export const getThemeColors = createAsyncThunk(
  "setting/getThemeColors",
  async (_, { rejectWithValue }) => {
    try {
      const response = await SettingService.getThemeColors();
      return response;
    } catch (err) {
      return rejectWithValue({
        message: err.message || "Error",
        messageKey: "theme_colors.fetch_error",
      });
    }
  },
);

export const updateThemeColors = createAsyncThunk(
  "setting/updateThemeColors",
  async (data, { rejectWithValue }) => {
    try {
      const response = await SettingService.updateThemeColors(data);
      return response;
    } catch (err) {
      return rejectWithValue({
        message: err.message || "Error",
        messageKey: "theme_colors.update_error",
      });
    }
  },
);

export const settingSlice = createSlice({
  name: "setting",
  initialState,
  reducers: {
    clearMessage: (state) => {
      state.message = "";
      state.messageKey = "";
      state.showMessage = false;
    },
    setSuccessMessage: (state, action) => {
      state.message = action.payload.message || "";
      state.messageKey = action.payload.messageKey || "";
      state.showMessage = true;
    },
    setErrorMessage: (state, action) => {
      state.message = action.payload.message || "";
      state.messageKey = action.payload.messageKey || "";
      state.showMessage = true;
    },
  },
  extraReducers: (builder) => {
    builder
      // Get Setting
      .addCase(getSetting.pending, (state) => {
        state.loading = true;
      })
      .addCase(getSetting.fulfilled, (state, { payload }) => {
        state.loading = false;
        state.settingData = payload;
      })
      .addCase(getSetting.rejected, (state, { payload }) => {
        state.loading = false;
        state.message = payload?.message || payload;
        state.messageKey = payload?.messageKey || "setting.fetch_error";
        state.showMessage = true;
      })

      // Update Setting
      .addCase(updateSetting.pending, (state) => {
        state.loading = true;
      })
      .addCase(updateSetting.fulfilled, (state, { payload }) => {
        state.loading = false;
        state.settingData = payload;
        state.messageKey = "setting.update_success";
        state.showMessage = true;
      })
      .addCase(updateSetting.rejected, (state, { payload }) => {
        state.loading = false;
        state.message = payload?.message || payload;
        state.messageKey = payload?.messageKey || "setting.update_error";
        state.showMessage = true;
      })

      // Get Reviews
      .addCase(getReviews.pending, (state) => {
        state.loading = true;
      })
      .addCase(getReviews.fulfilled, (state, { payload }) => {
        state.loading = false;
        state.reviews = payload;
        state.total = payload?.count || 0;
      })
      .addCase(getReviews.rejected, (state, { payload }) => {
        state.loading = false;
        state.reviews = [];
        state.message = payload?.message || payload;
        state.messageKey = payload?.messageKey || "setting.reviews_fetch_error";
        state.showMessage = true;
      })

      // Update Review Status
      .addCase(updateReviewStatus.fulfilled, (state, { payload }) => {
        if (state.reviews?.results) {
          state.reviews.results = state.reviews.results.map((r) =>
            r.review_id === payload.reviewId ? { ...r, status: payload.status } : r
          );
        }
      })

      // Delete Review
      .addCase(deleteReview.fulfilled, (state, { payload }) => {
        if (state.reviews?.results) {
          state.reviews.results = state.reviews.results.filter((r) => r.review_id !== payload);
          state.total = Math.max(0, (state.total || 1) - 1);
          if (state.reviews.count) state.reviews.count = Math.max(0, state.reviews.count - 1);
        }
      })

      // Get Zones
      .addCase(getZones.pending, (state) => {
        state.loading = true;
      })
      .addCase(getZones.fulfilled, (state, { payload }) => {
        state.loading = false;
        console.log("Zones payload:", payload); // Debug log

        // Ensure payload is an array and map zone_id to id for consistency
        const normalizedZones = Array.isArray(payload)
          ? payload.map((zone) => ({
              ...zone,
              id: zone.zone_id, // Map zone_id to id for Select component
            }))
          : [];

        state.zones = normalizedZones;
      })
      .addCase(getZones.rejected, (state, { payload }) => {
        state.loading = false;
        state.message = payload?.message || payload;
        state.messageKey = payload?.messageKey || "setting.zones_fetch_error";
        state.showMessage = true;
      })

      // Get Zone Delivery Prices - ALSO UPDATE THIS
      .addCase(getZoneDeliveryPrices.pending, (state) => {
        state.loading = true;
      })
      .addCase(getZoneDeliveryPrices.fulfilled, (state, { payload }) => {
        state.loading = false;

        // Handle nested response structure {success: true, data: [...]}
        const data = payload.data || payload;
        state.zoneDeliveryPrices = Array.isArray(data) ? data : [];

        console.log("Zone delivery prices loaded:", state.zoneDeliveryPrices);
      })
      .addCase(getZoneDeliveryPrices.rejected, (state, { payload }) => {
        state.loading = false;
        state.message = payload?.message || payload;
        state.messageKey = payload?.messageKey || "zone.fetch_error";
        state.showMessage = true;
      })

      // Create Zone Delivery Price
      .addCase(createZoneDeliveryPrice.pending, (state) => {
        state.loading = true;
      })
      .addCase(createZoneDeliveryPrice.fulfilled, (state, { payload }) => {
        state.loading = false;
        state.zoneDeliveryPrices.push(payload);
        state.messageKey = "zone.create_success";
        state.showMessage = true;
      })
      .addCase(createZoneDeliveryPrice.rejected, (state, { payload }) => {
        state.loading = false;
        state.message = payload?.message || payload;
        state.messageKey = payload?.messageKey || "zone.create_error";
        state.showMessage = true;
      })

      // Update Zone Delivery Price
      .addCase(updateZoneDeliveryPrice.pending, (state) => {
        state.loading = true;
      })
      .addCase(updateZoneDeliveryPrice.fulfilled, (state, { payload }) => {
        state.loading = false;

        // Find the index using zone_delivery_id
        const index = state.zoneDeliveryPrices.findIndex(
          (item) =>
            (item.zone_delivery_id || item.id) ===
            (payload.zone_delivery_id || payload.id),
        );

        if (index !== -1) {
          // Replace the existing item
          state.zoneDeliveryPrices[index] = payload;
        } else {
          // If not found, add it (shouldn't happen in update, but safety)
          state.zoneDeliveryPrices.push(payload);
        }

        state.messageKey = "zone.update_success";
        state.showMessage = true;
      })
      .addCase(updateZoneDeliveryPrice.rejected, (state, { payload }) => {
        state.loading = false;
        state.message = payload?.message || payload;
        state.messageKey = payload?.messageKey || "zone.update_error";
        state.showMessage = true;
      })

      // Delete Zone Delivery Price
      .addCase(deleteZoneDeliveryPrice.pending, (state) => {
        state.loading = true;
      })
      .addCase(deleteZoneDeliveryPrice.fulfilled, (state, { payload }) => {
        state.loading = false;

        // Filter using zone_delivery_id
        state.zoneDeliveryPrices = state.zoneDeliveryPrices.filter(
          (item) => (item.zone_delivery_id || item.id) !== payload,
        );

        state.messageKey = "zone.delete_success";
        state.showMessage = true;
      })
      .addCase(deleteZoneDeliveryPrice.rejected, (state, { payload }) => {
        state.loading = false;
        state.message = payload?.message || payload;
        state.messageKey = payload?.messageKey || "zone.delete_error";
        state.showMessage = true;
      })

      // Bulk Update Zone Delivery Prices
      .addCase(bulkUpdateZoneDeliveryPrices.pending, (state) => {
        state.loading = true;
      })
      .addCase(bulkUpdateZoneDeliveryPrices.fulfilled, (state, { payload }) => {
        state.loading = false;
        state.zoneDeliveryPrices = payload;
        state.messageKey = "zone.bulk_update_success";
        state.showMessage = true;
      })
      .addCase(bulkUpdateZoneDeliveryPrices.rejected, (state, { payload }) => {
        state.loading = false;
        state.message = payload?.message || payload;
        state.messageKey = payload?.messageKey || "zone.bulk_update_error";
        state.showMessage = true;
      })
      .addCase(getAreasByZone.pending, (state) => {
        state.loading = true;
      })
      .addCase(getAreasByZone.fulfilled, (state, { payload }) => {
        state.loading = false;
        state.areas = Array.isArray(payload) ? payload : [];
      })
      .addCase(getAreasByZone.rejected, (state, { payload }) => {
        state.loading = false;
        state.areas = [];
        state.message = payload?.message || payload;
        state.messageKey = payload?.messageKey || "area.fetch_error";
        state.showMessage = true;
      })

      // Area Delivery Prices
      .addCase(getAreaDeliveryPrices.pending, (state) => {
        state.loading = true;
      })
      .addCase(getAreaDeliveryPrices.fulfilled, (state, { payload }) => {
        state.loading = false;
        state.areaDeliveryPrices = payload;
      })
      .addCase(getAreaDeliveryPrices.rejected, (state, { payload }) => {
        state.loading = false;
        state.message = payload?.message || payload;
        state.messageKey = payload?.messageKey || "area.fetch_error";
        state.showMessage = true;
      })

      // Create Area Delivery Price
      .addCase(createAreaDeliveryPrice.pending, (state) => {
        state.loading = true;
      })
      .addCase(createAreaDeliveryPrice.fulfilled, (state, { payload }) => {
        state.loading = false;
        state.areaDeliveryPrices.push(payload);
        state.messageKey = "area.create_success";
        state.showMessage = true;
      })
      .addCase(createAreaDeliveryPrice.rejected, (state, { payload }) => {
        state.loading = false;
        state.message = payload?.message || payload;
        state.messageKey = payload?.messageKey || "area.create_error";
        state.showMessage = true;
      })

      // Update Area Delivery Price
      .addCase(updateAreaDeliveryPrice.pending, (state) => {
        state.loading = true;
      })
      .addCase(updateAreaDeliveryPrice.fulfilled, (state, { payload }) => {
        state.loading = false;
        const index = state.areaDeliveryPrices.findIndex(
          (item) => item.id === payload.id,
        );
        if (index !== -1) {
          state.areaDeliveryPrices[index] = payload;
        }
        state.messageKey = "area.update_success";
        state.showMessage = true;
      })
      .addCase(updateAreaDeliveryPrice.rejected, (state, { payload }) => {
        state.loading = false;
        state.message = payload?.message || payload;
        state.messageKey = payload?.messageKey || "area.update_error";
        state.showMessage = true;
      })

      // Delete Area Delivery Price
      .addCase(deleteAreaDeliveryPrice.pending, (state) => {
        state.loading = true;
      })
      .addCase(deleteAreaDeliveryPrice.fulfilled, (state, { payload }) => {
        state.loading = false;
        state.areaDeliveryPrices = state.areaDeliveryPrices.filter(
          (item) => item.id !== payload,
        );
        state.messageKey = "area.delete_success";
        state.showMessage = true;
      })
      .addCase(deleteAreaDeliveryPrice.rejected, (state, { payload }) => {
        state.loading = false;
        state.message = payload?.message || payload;
        state.messageKey = payload?.messageKey || "area.delete_error";
        state.showMessage = true;
      })

      // Bulk Create Area Delivery Prices
      .addCase(bulkCreateAreaDeliveryPrices.pending, (state) => {
        state.loading = true;
      })
      .addCase(bulkCreateAreaDeliveryPrices.fulfilled, (state, { payload }) => {
        state.loading = false;
        if (Array.isArray(payload)) {
          state.areaDeliveryPrices = [...state.areaDeliveryPrices, ...payload];
        }
        state.messageKey = "area.bulk_create_success";
        state.showMessage = true;
      })
      .addCase(bulkCreateAreaDeliveryPrices.rejected, (state, { payload }) => {
        state.loading = false;
        state.message = payload?.message || payload;
        state.messageKey = payload?.messageKey || "area.bulk_create_error";
        state.showMessage = true;
      })

      .addCase(getStripeKeys.pending, (state) => {
        state.loading = true;
      })
      .addCase(getStripeKeys.fulfilled, (state, { payload }) => {
        state.loading = false;
        state.stripeKeys = payload;
      })
      .addCase(getStripeKeys.rejected, (state, { payload }) => {
        state.loading = false;
        state.message = payload?.message || payload;
        state.messageKey = payload?.messageKey || "setting.stripe_keys_error";
        state.showMessage = true;
      })
      .addCase(updateStripeKeys.pending, (state) => {
        state.loading = true;
      })
      .addCase(updateStripeKeys.fulfilled, (state, { payload }) => {
        state.loading = false;
        state.stripeKeys = payload;
        state.messageKey = "setting.stripe_update_success";
        state.showMessage = true;
      })
      .addCase(updateStripeKeys.rejected, (state, { payload }) => {
        state.loading = false;
        state.message = payload?.message || payload;
        state.messageKey = payload?.messageKey || "setting.stripe_update_error";
        state.showMessage = true;
      })
      // CBK Keys
      .addCase(getCbkKeys.pending, (state) => {
        state.loading = true;
      })
      .addCase(getCbkKeys.fulfilled, (state, { payload }) => {
        state.loading = false;
        state.cbkKeys = payload;
      })
      .addCase(getCbkKeys.rejected, (state, { payload }) => {
        state.loading = false;
        state.message = payload?.message || payload;
        state.messageKey = payload?.messageKey || "setting.cbk_keys_error";
        state.showMessage = true;
      })

      // Update CBK Keys
      .addCase(updateCbkKeys.pending, (state) => {
        state.loading = true;
      })
      .addCase(updateCbkKeys.fulfilled, (state, { payload }) => {
        state.loading = false;
        if (payload.data) {
          state.cbkKeys = payload.data;
        }
        state.messageKey = "setting.cbk_update_success";
        state.showMessage = true;
      })
      .addCase(updateCbkKeys.rejected, (state, { payload }) => {
        state.loading = false;
        state.message = payload?.message || payload;
        state.messageKey = payload?.messageKey || "setting.cbk_update_error";
        state.showMessage = true;
      })
      // Tabby Keys
      .addCase(getTabbyKeys.pending, (state) => {
        state.loading = true;
      })
      .addCase(getTabbyKeys.fulfilled, (state, { payload }) => {
        state.loading = false;
        state.tabbyKeys = payload;
      })
      .addCase(getTabbyKeys.rejected, (state, { payload }) => {
        state.loading = false;
        state.message = payload?.message || payload;
        state.messageKey = payload?.messageKey || "setting.tabby_keys_error";
        state.showMessage = true;
      })
      // Update Tabby Keys
      .addCase(updateTabbyKeys.pending, (state) => {
        state.loading = true;
      })
      .addCase(updateTabbyKeys.fulfilled, (state, { payload }) => {
        state.loading = false;
        if (payload.data) {
          state.tabbyKeys = payload.data;
        }
        state.messageKey = "setting.tabby_update_success";
        state.showMessage = true;
      })
      .addCase(updateTabbyKeys.rejected, (state, { payload }) => {
        state.loading = false;
        state.message = payload?.message || payload;
        state.messageKey = payload?.messageKey || "setting.tabby_update_error";
        state.showMessage = true;
      })
      // UPayment Keys
      .addCase(getUPaymentKeys.pending, (state) => {
        state.loading = true;
      })
      .addCase(getUPaymentKeys.fulfilled, (state, { payload }) => {
        state.loading = false;
        state.upaymentKeys = payload;
      })
      .addCase(getUPaymentKeys.rejected, (state, { payload }) => {
        state.loading = false;
        state.message = payload?.message || payload;
        state.messageKey = payload?.messageKey || "setting.upayment_keys_error";
        state.showMessage = true;
      })
      // Update UPayment Keys
      .addCase(updateUPaymentKeys.pending, (state) => {
        state.loading = true;
      })
      .addCase(updateUPaymentKeys.fulfilled, (state, { payload }) => {
        state.loading = false;
        if (payload.data) {
          state.upaymentKeys = payload.data;
        }
        state.messageKey = "setting.upayment_update_success";
        state.showMessage = true;
      })
      .addCase(updateUPaymentKeys.rejected, (state, { payload }) => {
        state.loading = false;
        state.message = payload?.message || payload;
        state.messageKey =
          payload?.messageKey || "setting.upayment_update_error";
        state.showMessage = true;
      })
      // Taly Keys
      .addCase(getTalyKeys.pending, (state) => {
        state.loading = true;
      })
      .addCase(getTalyKeys.fulfilled, (state, { payload }) => {
        state.loading = false;
        state.talyKeys = payload;
      })
      .addCase(getTalyKeys.rejected, (state, { payload }) => {
        state.loading = false;
        state.message = payload?.message || payload;
        state.messageKey = payload?.messageKey || "setting.taly_keys_error";
        state.showMessage = true;
      })
      // Update Taly Keys
      .addCase(updateTalyKeys.pending, (state) => {
        state.loading = true;
      })
      .addCase(updateTalyKeys.fulfilled, (state, { payload }) => {
        state.loading = false;
        if (payload.data) {
          state.talyKeys = payload.data;
        }
        state.messageKey = "setting.taly_update_success";
        state.showMessage = true;
      })
      .addCase(updateTalyKeys.rejected, (state, { payload }) => {
        state.loading = false;
        state.message = payload?.message || payload;
        state.messageKey = payload?.messageKey || "setting.taly_update_error";
        state.showMessage = true;
      })
      // Allowmena shipping
      .addCase(getAllowmenaKeys.pending, (state) => {
        state.loading = true;
      })
      .addCase(getAllowmenaKeys.fulfilled, (state, { payload }) => {
        state.loading = false;
        state.allowmenaKeys = payload;
      })
      .addCase(getAllowmenaKeys.rejected, (state, { payload }) => {
        state.loading = false;
        state.message = payload?.message || payload;
        state.messageKey =
          payload?.messageKey || "setting.allowmena_keys_error";
        state.showMessage = true;
      })
      .addCase(updateAllowmenaKeys.pending, (state) => {
        state.loading = true;
      })
      .addCase(updateAllowmenaKeys.fulfilled, (state, { payload }) => {
        state.loading = false;
        if (payload.data) {
          state.allowmenaKeys = payload.data;
        }
        state.messageKey = "setting.allowmena_update_success";
        state.showMessage = true;
      })
      .addCase(updateAllowmenaKeys.rejected, (state, { payload }) => {
        state.loading = false;
        state.message = payload?.message || payload;
        state.messageKey =
          payload?.messageKey || "setting.allowmena_update_error";
        state.showMessage = true;
      })
      // Tehseel Keys
      .addCase(getTehseelKeys.pending, (state) => {
        state.loading = true;
      })
      .addCase(getTehseelKeys.fulfilled, (state, { payload }) => {
        state.loading = false;
        state.tehseelKeys = payload;
      })
      .addCase(getTehseelKeys.rejected, (state, { payload }) => {
        state.loading = false;
        state.message = payload?.message || payload;
        state.messageKey = payload?.messageKey || "setting.tehseel_keys_error";
        state.showMessage = true;
      })
      // Update Tehseel Keys
      .addCase(updateTehseelKeys.pending, (state) => {
        state.loading = true;
      })
      .addCase(updateTehseelKeys.fulfilled, (state, { payload }) => {
        state.loading = false;
        if (payload.data) {
          state.tehseelKeys = payload.data;
        }
        state.messageKey = "setting.tehseel_update_success";
        state.showMessage = true;
      })
      .addCase(updateTehseelKeys.rejected, (state, { payload }) => {
        state.loading = false;
        state.message = payload?.message || payload;
        state.messageKey = payload?.messageKey || "setting.tehseel_update_error";
        state.showMessage = true;
      })
      // Email Settings
      .addCase(getEmailSettings.pending, (state) => {
        state.loading = true;
      })
      .addCase(getEmailSettings.fulfilled, (state, { payload }) => {
        state.loading = false;
        state.emailSettings = payload;
      })
      .addCase(getEmailSettings.rejected, (state, { payload }) => {
        state.loading = false;
        state.message = payload?.message || payload;
        state.messageKey = payload?.messageKey || "email_settings.fetch_error";
        state.showMessage = true;
      })

      // Update Email Settings
      .addCase(updateEmailSettings.pending, (state) => {
        state.loading = true;
      })
      .addCase(updateEmailSettings.fulfilled, (state, { payload }) => {
        state.loading = false;
        state.emailSettings = payload;
        state.messageKey = "email_settings.update_success";
        state.showMessage = true;
      })
      .addCase(updateEmailSettings.rejected, (state, { payload }) => {
        state.loading = false;
        state.message = payload?.message || payload;
        state.messageKey = payload?.messageKey || "email_settings.update_error";
        state.showMessage = true;
      })

      // Reset Email Settings
      .addCase(resetEmailSettings.pending, (state) => {
        state.loading = true;
      })
      .addCase(resetEmailSettings.fulfilled, (state) => {
        state.loading = false;
        state.emailSettings = {};
        state.messageKey = "email_settings.reset_success";
        state.showMessage = true;
      })
      .addCase(resetEmailSettings.rejected, (state, { payload }) => {
        state.loading = false;
        state.message = payload?.message || payload;
        state.messageKey = payload?.messageKey || "email_settings.reset_error";
        state.showMessage = true;
      })
      .addCase(getCountrySettings.pending, (state) => {
        state.loading = true;
      })
      .addCase(getCountrySettings.fulfilled, (state, { payload }) => {
        state.loading = false;
        console.log("Country settings payload:", payload);

        const data = payload?.data || payload;

        state.countrySettings = {
          assigned_country_ids: data?.assigned_country_ids || [],
          assigned_countries: data?.assigned_countries || [],
          all_countries: data?.all_countries || [],
          zones: data?.zones || [],
          areas: data?.areas || [],
        };

        console.log("Updated countrySettings:", state.countrySettings);
      })
      .addCase(getCountrySettings.rejected, (state, { payload }) => {
        state.loading = false;
        state.message = payload?.message || "Error";
        state.messageKey =
          payload?.messageKey || "country_settings_fetch_error";
        state.showMessage = true;
      })

      // Update Country Settings
      .addCase(updateCountrySettings.pending, (state) => {
        state.loading = true;
      })
      .addCase(updateCountrySettings.fulfilled, (state, { payload }) => {
        state.loading = false;
        console.log("Update country settings payload:", payload);

        const data = payload?.data || payload;

        if (data) {
          state.countrySettings = {
            ...state.countrySettings,
            assigned_country_ids: data?.assigned_country_ids || [],
            assigned_countries: data?.assigned_countries || [],
            zones: data?.zones || [],
            areas: data?.areas || [],
          };
        }
        state.messageKey = "country_settings_update_success";
        state.showMessage = true;
      })
      .addCase(updateCountrySettings.rejected, (state, { payload }) => {
        state.loading = false;
        state.message = payload?.message || "Error";
        state.messageKey =
          payload?.messageKey || "country_settings_update_error";
        state.showMessage = true;
      })

      // Delete Country Settings
      .addCase(deleteCountrySettings.pending, (state) => {
        state.loading = true;
      })
      .addCase(deleteCountrySettings.fulfilled, (state) => {
        state.loading = false;
        state.countrySettings = {
          ...state.countrySettings,
          assigned_country_ids: [],
          assigned_countries: [],
          zones: [],
          areas: [],
        };
        state.messageKey = "country_settings_delete_success";
        state.showMessage = true;
      })
      .addCase(deleteCountrySettings.rejected, (state, { payload }) => {
        state.loading = false;
        state.message = payload?.message || "Error";
        state.messageKey =
          payload?.messageKey || "country_settings_delete_error";
        state.showMessage = true;
      })

      // Get Country Zones
      .addCase(getCountryZones.pending, (state) => {
        state.loading = true;
      })
      .addCase(getCountryZones.fulfilled, (state, { payload }) => {
        state.loading = false;
        console.log("Country zones payload:", payload);

        const data = payload?.data || payload;
        state.countryZones = Array.isArray(data) ? data : [];
      })
      .addCase(getCountryZones.rejected, (state, { payload }) => {
        state.loading = false;
        state.message = payload?.message || "Error";
        state.messageKey =
          payload?.messageKey || "country_settings_zones_fetch_error";
        state.showMessage = true;
      })

      // Managed Areas
      .addCase(getManagedAreas.pending, (state) => {
        state.loading = true;
      })
      .addCase(getManagedAreas.fulfilled, (state, { payload }) => {
        state.loading = false;
        const data = payload?.data || payload;
        state.managedAreas = Array.isArray(data) ? data : [];
      })
      .addCase(getManagedAreas.rejected, (state, { payload }) => {
        state.loading = false;
        state.managedAreas = [];
        state.message = payload?.message || "Error";
        state.messageKey = payload?.messageKey || "area_fetch_error";
        state.showMessage = true;
      })

      // Create Managed Area
      .addCase(createManagedArea.pending, (state) => {
        state.loading = true;
      })
      .addCase(createManagedArea.fulfilled, (state, { payload }) => {
        state.loading = false;
        const data = payload?.data || payload;
        if (data) {
          state.managedAreas.push(data);
        }
        state.messageKey = "area_create_success";
        state.showMessage = true;
      })
      .addCase(createManagedArea.rejected, (state, { payload }) => {
        state.loading = false;
        state.message = payload?.message || "Error";
        state.messageKey = payload?.messageKey || "area_create_error";
        state.showMessage = true;
      })

      // Update Managed Area
      .addCase(updateManagedArea.pending, (state) => {
        state.loading = true;
      })
      .addCase(updateManagedArea.fulfilled, (state, { payload }) => {
        state.loading = false;
        const data = payload?.data || payload;
        if (data) {
          const index = state.managedAreas.findIndex(
            (area) => area.area_id === data.area_id,
          );
          if (index !== -1) {
            state.managedAreas[index] = data;
          }
        }
        state.messageKey = "area_update_success";
        state.showMessage = true;
      })
      .addCase(updateManagedArea.rejected, (state, { payload }) => {
        state.loading = false;
        state.message = payload?.message || "Error";
        state.messageKey = payload?.messageKey || "area_update_error";
        state.showMessage = true;
      })

      // Delete Managed Area
      .addCase(deleteManagedArea.pending, (state) => {
        state.loading = true;
      })
      .addCase(deleteManagedArea.fulfilled, (state, { payload }) => {
        state.loading = false;
        state.managedAreas = state.managedAreas.filter(
          (area) => area.area_id !== payload,
        );
        state.messageKey = "area_delete_success";
        state.showMessage = true;
      })
      .addCase(deleteManagedArea.rejected, (state, { payload }) => {
        state.loading = false;
        state.message = payload?.message || "Error";
        state.messageKey = payload?.messageKey || "area_delete_error";
        state.showMessage = true;
      })

      // Bulk Create Managed Areas
      .addCase(bulkCreateManagedAreas.pending, (state) => {
        state.loading = true;
      })
      .addCase(bulkCreateManagedAreas.fulfilled, (state, { payload }) => {
        state.loading = false;
        const data = payload?.data || payload;
        if (data?.created_areas && Array.isArray(data.created_areas)) {
          state.managedAreas = [...state.managedAreas, ...data.created_areas];
        }
        state.messageKey = "area_bulk_create_success";
        state.showMessage = true;
      })
      .addCase(bulkCreateManagedAreas.rejected, (state, { payload }) => {
        state.loading = false;
        state.message = payload?.message || "Error";
        state.messageKey = payload?.messageKey || "area_bulk_create_error";
        state.showMessage = true;
      })
      .addCase(getShippingConfig.pending, (state) => {
        state.loading = true;
      })
      .addCase(getShippingConfig.fulfilled, (state, { payload }) => {
        state.loading = false;
        const data = payload?.data || payload;
        state.shippingConfig = data?.shipping_config || [];
        state.legacySettings = data?.legacy_settings || [];
        state.countrySettings = {
          ...state.countrySettings,
          assigned_country_ids: data?.assigned_country_ids || [],
        };
      })
      .addCase(getShippingConfig.rejected, (state, { payload }) => {
        state.loading = false;
        state.message = payload?.message || "Error";
        state.messageKey = payload?.messageKey || "shipping.fetch_error";
        state.showMessage = true;
      })

      // Country Shipping
      .addCase(getCountryShipping.pending, (state) => {
        state.loading = true;
      })
      .addCase(getCountryShipping.fulfilled, (state, { payload }) => {
        state.loading = false;
        const data = payload?.data || payload;
        state.countryShipping = Array.isArray(data) ? data : data ? [data] : [];
      })
      .addCase(getCountryShipping.rejected, (state, { payload }) => {
        state.loading = false;
        state.message = payload?.message || "Error";
        state.messageKey =
          payload?.messageKey || "shipping.country_fetch_error";
        state.showMessage = true;
      })

      .addCase(updateCountryShipping.pending, (state) => {
        state.loading = true;
      })
      .addCase(updateCountryShipping.fulfilled, (state, { payload }) => {
        state.loading = false;
        const data = payload?.data || payload;
        if (data) {
          const index = state.countryShipping.findIndex(
            (item) => item.country_id === data.country_id,
          );
          if (index !== -1) {
            state.countryShipping[index] = data;
          } else {
            state.countryShipping.push(data);
          }
        }
        state.messageKey = "shipping.country_update_success";
        state.showMessage = true;
      })
      .addCase(updateCountryShipping.rejected, (state, { payload }) => {
        state.loading = false;
        state.message = payload?.message || "Error";
        state.messageKey =
          payload?.messageKey || "shipping.country_update_error";
        state.showMessage = true;
      })

      .addCase(deleteCountryShipping.pending, (state) => {
        state.loading = true;
      })
      .addCase(deleteCountryShipping.fulfilled, (state, { payload }) => {
        state.loading = false;
        state.countryShipping = state.countryShipping.filter(
          (item) => item.country_id !== payload,
        );
        state.messageKey = "shipping.country_delete_success";
        state.showMessage = true;
      })
      .addCase(deleteCountryShipping.rejected, (state, { payload }) => {
        state.loading = false;
        state.message = payload?.message || "Error";
        state.messageKey =
          payload?.messageKey || "shipping.country_delete_error";
        state.showMessage = true;
      })

      // Zone Shipping
      .addCase(getZoneShipping.pending, (state) => {
        state.loading = true;
      })
      .addCase(getZoneShipping.fulfilled, (state, { payload }) => {
        state.loading = false;
        const data = payload?.data || payload;
        state.zoneShipping = Array.isArray(data) ? data : data ? [data] : [];
      })
      .addCase(getZoneShipping.rejected, (state, { payload }) => {
        state.loading = false;
        state.message = payload?.message || "Error";
        state.messageKey = payload?.messageKey || "shipping.zone_fetch_error";
        state.showMessage = true;
      })

      .addCase(updateZoneShipping.pending, (state) => {
        state.loading = true;
      })
      .addCase(updateZoneShipping.fulfilled, (state, { payload }) => {
        state.loading = false;
        const data = payload?.data || payload;
        if (data) {
          const index = state.zoneShipping.findIndex(
            (item) => item.zone_id === data.zone_id,
          );
          if (index !== -1) {
            state.zoneShipping[index] = data;
          } else {
            state.zoneShipping.push(data);
          }
        }
        state.messageKey = "shipping.zone_update_success";
        state.showMessage = true;
      })
      .addCase(updateZoneShipping.rejected, (state, { payload }) => {
        state.loading = false;
        state.message = payload?.message || "Error";
        state.messageKey = payload?.messageKey || "shipping.zone_update_error";
        state.showMessage = true;
      })

      .addCase(deleteZoneShipping.pending, (state) => {
        state.loading = true;
      })
      .addCase(deleteZoneShipping.fulfilled, (state, { payload }) => {
        state.loading = false;
        state.zoneShipping = state.zoneShipping.filter(
          (item) => item.zone_id !== payload,
        );
        state.messageKey = "shipping.zone_delete_success";
        state.showMessage = true;
      })
      .addCase(deleteZoneShipping.rejected, (state, { payload }) => {
        state.loading = false;
        state.message = payload?.message || "Error";
        state.messageKey = payload?.messageKey || "shipping.zone_delete_error";
        state.showMessage = true;
      })

      // Area Shipping
      .addCase(getAreaShipping.pending, (state) => {
        state.loading = true;
      })
      .addCase(getAreaShipping.fulfilled, (state, { payload }) => {
        state.loading = false;
        const data = payload?.data || payload;
        state.areaShipping = Array.isArray(data) ? data : data ? [data] : [];
      })
      .addCase(getAreaShipping.rejected, (state, { payload }) => {
        state.loading = false;
        state.message = payload?.message || "Error";
        state.messageKey = payload?.messageKey || "shipping.area_fetch_error";
        state.showMessage = true;
      })

      .addCase(updateAreaShipping.pending, (state) => {
        state.loading = true;
      })
      .addCase(updateAreaShipping.fulfilled, (state, { payload }) => {
        state.loading = false;
        const data = payload?.data || payload;
        if (data) {
          const index = state.areaShipping.findIndex(
            (item) => item.area_id === data.area_id,
          );
          if (index !== -1) {
            state.areaShipping[index] = data;
          } else {
            state.areaShipping.push(data);
          }
        }
        state.messageKey = "shipping.area_update_success";
        state.showMessage = true;
      })
      .addCase(updateAreaShipping.rejected, (state, { payload }) => {
        state.loading = false;
        state.message = payload?.message || "Error";
        state.messageKey = payload?.messageKey || "shipping.area_update_error";
        state.showMessage = true;
      })

      .addCase(deleteAreaShipping.pending, (state) => {
        state.loading = true;
      })
      .addCase(deleteAreaShipping.fulfilled, (state, { payload }) => {
        state.loading = false;
        state.areaShipping = state.areaShipping.filter(
          (item) => item.area_id !== payload,
        );
        state.messageKey = "shipping.area_delete_success";
        state.showMessage = true;
      })
      .addCase(deleteAreaShipping.rejected, (state, { payload }) => {
        state.loading = false;
        state.message = payload?.message || "Error";
        state.messageKey = payload?.messageKey || "shipping.area_delete_error";
        state.showMessage = true;
      })

      // Bulk Shipping
      .addCase(bulkUpdateShipping.pending, (state) => {
        state.loading = true;
      })
      .addCase(bulkUpdateShipping.fulfilled, (state, { payload }) => {
        state.loading = false;
        state.messageKey = "shipping.bulk_update_success";
        state.showMessage = true;
      })
      .addCase(bulkUpdateShipping.rejected, (state, { payload }) => {
        state.loading = false;
        state.message = payload?.message || "Error";
        state.messageKey = payload?.messageKey || "shipping.bulk_update_error";
        state.showMessage = true;
      })

      // Footer Settings
      .addCase(getFooterSettings.pending, (state) => {
        state.loading = true;
      })
      .addCase(getFooterSettings.fulfilled, (state, { payload }) => {
        state.loading = false;
        state.footerSettings = payload?.data || payload;
      })
      .addCase(getFooterSettings.rejected, (state, { payload }) => {
        state.loading = false;
        state.message = payload?.message || "Error";
        state.messageKey = payload?.messageKey || "footer_settings.fetch_error";
        state.showMessage = true;
      })

      // Update Footer Settings
      .addCase(updateFooterSettings.pending, (state) => {
        state.loading = true;
      })
      .addCase(updateFooterSettings.fulfilled, (state, { payload }) => {
        state.loading = false;
        state.footerSettings = payload?.data || payload;
        state.messageKey = "footer_settings.update_success";
        state.showMessage = true;
      })
      .addCase(updateFooterSettings.rejected, (state, { payload }) => {
        state.loading = false;
        state.message = payload?.message || "Error";
        state.messageKey =
          payload?.messageKey || "footer_settings.update_error";
        state.showMessage = true;
      })

      // Generate Footer Code (AI)
      .addCase(generateFooterCode.pending, (state) => {
        state.generatingFooterCode = true;
      })
      .addCase(generateFooterCode.fulfilled, (state, { payload }) => {
        state.generatingFooterCode = false;
        const data = payload?.data || payload;
        state.generatedFooterCode = data?.code || "";
        state.messageKey = "footer_builder.generate_success";
        state.showMessage = true;
      })
      .addCase(generateFooterCode.rejected, (state, { payload }) => {
        state.generatingFooterCode = false;
        state.message = payload?.message || "Error";
        state.messageKey =
          payload?.messageKey || "footer_builder.generate_error";
        state.showMessage = true;
      })

      // Header Settings
      .addCase(getHeaderSettings.pending, (state) => {
        state.loading = true;
      })
      .addCase(getHeaderSettings.fulfilled, (state, { payload }) => {
        state.loading = false;
        state.headerSettings = payload?.data || payload;
      })
      .addCase(getHeaderSettings.rejected, (state, { payload }) => {
        state.loading = false;
        state.message = payload?.message || "Error";
        state.messageKey = payload?.messageKey || "header_settings.fetch_error";
        state.showMessage = true;
      })

      // Update Header Settings
      .addCase(updateHeaderSettings.pending, (state) => {
        state.loading = true;
      })
      .addCase(updateHeaderSettings.fulfilled, (state, { payload }) => {
        state.loading = false;
        state.headerSettings = payload?.data || payload;
        state.messageKey = "header_settings.update_success";
        state.showMessage = true;
      })
      .addCase(updateHeaderSettings.rejected, (state, { payload }) => {
        state.loading = false;
        state.message = payload?.message || "Error";
        state.messageKey =
          payload?.messageKey || "header_settings.update_error";
        state.showMessage = true;
      })

      // SMS Settings
      .addCase(getSmsSettings.pending, (state) => {
        state.smsLoading = true;
      })
      .addCase(getSmsSettings.fulfilled, (state, { payload }) => {
        state.smsLoading = false;
        state.smsSettings = payload?.data || payload;
      })
      .addCase(getSmsSettings.rejected, (state, { payload }) => {
        state.smsLoading = false;
        state.message = payload?.message || "Error";
        state.showMessage = true;
      })

      .addCase(updateSmsSettings.pending, (state) => {
        state.smsLoading = true;
      })
      .addCase(updateSmsSettings.fulfilled, (state) => {
        state.smsLoading = false;
      })
      .addCase(updateSmsSettings.rejected, (state, { payload }) => {
        state.smsLoading = false;
        state.message = payload?.message || "Error";
        state.showMessage = true;
      })

      // Pixel Settings
      .addCase(getPixelSettings.pending, (state) => {
        state.pixelLoading = true;
      })
      .addCase(getPixelSettings.fulfilled, (state, { payload }) => {
        state.pixelLoading = false;
        state.pixelSettings = payload?.data || payload;
      })
      .addCase(getPixelSettings.rejected, (state, { payload }) => {
        state.pixelLoading = false;
        state.message = payload?.message || "Error";
        state.showMessage = true;
      })

      .addCase(updatePixelSettings.pending, (state) => {
        state.pixelLoading = true;
      })
      .addCase(updatePixelSettings.fulfilled, (state) => {
        state.pixelLoading = false;
        state.messageKey = "pixel_settings.update_success";
        state.showMessage = true;
      })
      .addCase(updatePixelSettings.rejected, (state, { payload }) => {
        state.pixelLoading = false;
        state.message = payload?.message || "Error";
        state.showMessage = true;
      })

      .addCase(getFirebaseWebSettings.pending, (state) => {
        state.firebaseWebLoading = true;
      })
      .addCase(getFirebaseWebSettings.fulfilled, (state, { payload }) => {
        state.firebaseWebLoading = false;
        state.firebaseWebSettings = payload?.data ?? payload;
      })
      .addCase(getFirebaseWebSettings.rejected, (state, { payload }) => {
        state.firebaseWebLoading = false;
        state.message = payload?.message || "Error";
        state.showMessage = true;
      })
      .addCase(updateFirebaseWebSettings.pending, (state) => {
        state.firebaseWebLoading = true;
      })
      .addCase(updateFirebaseWebSettings.fulfilled, (state) => {
        state.firebaseWebLoading = false;
        state.messageKey = "firebase_settings.update_success";
        state.showMessage = true;
      })
      .addCase(updateFirebaseWebSettings.rejected, (state, { payload }) => {
        state.firebaseWebLoading = false;
        state.message = payload?.message || "Error";
        state.showMessage = true;
      })

      .addCase(getThemeColors.pending, (state) => {
        state.themeColorsLoading = true;
      })
      .addCase(getThemeColors.fulfilled, (state, { payload }) => {
        state.themeColorsLoading = false;
        state.themeColors = payload?.data || payload;
      })
      .addCase(getThemeColors.rejected, (state, { payload }) => {
        state.themeColorsLoading = false;
        state.message = payload?.message || "Error";
        state.showMessage = true;
      })

      .addCase(updateThemeColors.pending, (state) => {
        state.themeColorsLoading = true;
      })
      .addCase(updateThemeColors.fulfilled, (state) => {
        state.themeColorsLoading = false;
        state.messageKey = "theme_colors.update_success";
        state.showMessage = true;
      })
      .addCase(updateThemeColors.rejected, (state, { payload }) => {
        state.themeColorsLoading = false;
        state.message = payload?.message || "Error";
        state.showMessage = true;
      });
  },
});

export const { clearMessage, setSuccessMessage, setErrorMessage } =
  settingSlice.actions;
export default settingSlice.reducer;
